import {
    createSlice,
    createAsyncThunk,
    createEntityAdapter,
} from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const escalationAdapter = createEntityAdapter({
    selectId: (escalation) => escalation._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
});

// Async Thunks
export const fetchEscalationData = createAsyncThunk(
    "escalation/fetch",
    async ({ startDate, endDate }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/user/getEscalations",
                method: "GET",
                params: { startDate, endDate },
            });
            if (!response.success || !response?.data?.length > 0) {
                return rejectWithValue(response.message || "Failed to fetch escalations");
            }

            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch escalations");
        }
    }
);

export const createEscalation = createAsyncThunk(
    "escalation/create",
    async (data, { rejectWithValue }) => {
        try {
            // Construct FormData for file upload        
            const response = await request({
                endpoint: "/user/createEscalation",
                method: "POST",
                body: data,
                headers: {
                    "Content-Type": "multipart/form-data",
                }
                // Do not set "Content-Type"; Fetch API/axios will handle it automatically for FormData
            });

            console.log("Create Escalation API Response:", response);

            if (!response.success || !response.data) {
                return rejectWithValue(response.error.message || "Failed to create escalation.");
            }

            return response.data; // Return the escalation data on success
        } catch ({ error }) {
            console.error("Error creating escalation:", error.message || error);
            return rejectWithValue(error.message || error || "Failed to create escalation.");
        }
    }
);
export const replyEscalation = createAsyncThunk(
    "escalation/reply",
    async ({ data, escalationId }, { rejectWithValue }) => {
        try {
            // Construct FormData for file upload        
            const response = await request({
                endpoint: "/user/replyEscalation",
                method: "POST",
                body: data,
                params: { escalationId },
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            });

            console.log("Reply Escalation API Response:", response);

            if (!response.success || !response.data) {
                return rejectWithValue(response.error.message || "Failed to Reply escalation.");
            }

            return response.data; // Return the escalation data on success
        } catch ({ error }) {
            console.error("Error Reply escalation:", error.message || error);
            return rejectWithValue(error.message || error || "Failed to Reply escalation.");
        }
    }
);
export const callRequest = createAsyncThunk(
    "escalation/call",
    async (data, { rejectWithValue }) => {
        try {
            // Construct FormData for file upload
            const response = await request({
                endpoint: "/user/createRequestCallback",
                method: "POST",
                body: data,

            });
            console.log("Create Call Request API Response:", response);

            if (!response.success || !response.data) {
                return rejectWithValue(response.error.message || "Failed to create Call Request.");
            }

            return response.data; // Return the escalation data on success
        }
        catch ({ error }) {
            console.error("Error creating Call Request:", error.message || error);
            return rejectWithValue(error.message || error || "Failed to create Call Request.");
        }
    }

)

// Initial state
const initialState = escalationAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const escalationSlice = createSlice({
    name: "escalation",
    initialState,
    reducers: {
        clearEscalations: (state) => {
            escalationAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
        addAllEscalationsIO: (state, action) => {
            escalationAdapter.addMany(state, action.payload);
        },
        addEscalationOneIO: (state, action) => {
            escalationAdapter.addOne(state, action.payload);
        },
        updateEscalationOneIO: (state, action) => {
            escalationAdapter.upsertOne(state, action.payload);
        },
        deleteEscalationOneIO: (state, action) => {
            escalationAdapter.removeOne(state, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEscalationData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEscalationData.fulfilled, (state, action) => {
                escalationAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchEscalationData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create Escalation
            .addCase(createEscalation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createEscalation.fulfilled, (state, action) => {
                escalationAdapter.addOne(state, action.payload);
                state.loading = false;
            })
            .addCase(createEscalation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Reply Escalation
            .addCase(replyEscalation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(replyEscalation.fulfilled, (state, action) => {
                escalationAdapter.updateOne(state, {
                    id: action.payload.escalation._id,
                    changes: {
                        chat: [...state.entities[action.payload.escalation._id].chat, action.payload.message]
                    }
                });
                state.loading = false;
            })
            .addCase(replyEscalation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            //call request
            .addCase(callRequest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(callRequest.fulfilled, (state, action) => {
                escalationAdapter.addOne(state, action.payload);
                state.loading = false;
            })
            .addCase(callRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

    }

});

// Update selectors export
export const {
    selectAll: selectAllEscalations,
    selectById: selectEscalationById,
    selectIds: selectEscalationIds,
} = escalationAdapter.getSelectors((state) => state.escalation);

// Update actions export
export const { clearEscalations, addAllEscalationsIO, addEscalationOneIO, updateEscalationOneIO, deleteEscalationOneIO } = escalationSlice.actions;
export default escalationSlice.reducer;
